import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Plus } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISAL_CALIBRATION_SESSIONS, APPRAISAL_CYCLES, EMPLOYEE_DEPARTMENT,
} from "../../constants/api";

const STATUS_TONE = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    cancelled: "bg-slate-200 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",
};


/* Master endpoints go through tools.utils.paginator, which returns a bare list
   only when `drop_down` is set and a {result, total_count} object otherwise.
   Ask for the list, and unwrap defensively anyway -- a shape mismatch should
   degrade to an empty dropdown, not take the page down with it. */
const asList = (data) => (Array.isArray(data) ? data : data?.result || []);

const CalibrationSessionList = () => {
    const navigate = useNavigate();
    const { fetch, create } = useApiMethods();
    const [sessions, setSessions] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: "", cycle_id: null, department_id: null, scheduled_on: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_CALIBRATION_SESSIONS);
            setSessions(res?.data || []);
        } catch (_) {
            setSessions([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        fetch(APPRAISAL_CYCLES).then((r) => setCycles(r?.data || [])).catch(() => { });
        fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" })
            .then((r) => setDepartments(asList(r?.data)))
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const save = async () => {
        if (!form.name.trim() || !form.cycle_id) {
            ToastMsg("error", "A name and a cycle are required");
            return;
        }
        setSaving(true);
        try {
            const res = await create(APPRAISAL_CALIBRATION_SESSIONS, form);
            if (res?.status === "success") {
                ToastMsg("success", "Session created");
                setAdding(false);
                setForm({ name: "", cycle_id: null, department_id: null, scheduled_on: null });
                navigate(`/appraisal/calibration/${res.data.id}`);
            } else {
                ToastMsg("error", res?.message || "Could not create the session");
            }
        } catch (_) {
            ToastMsg("error", "Could not create the session");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Scale}
                title="Calibration"
                subtitle="Compare ratings across a cohort before publishing"
                totalRecords={sessions.length}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex justify-end">
                    {!adding && (
                        <Button variant="brand" size="sm" onClick={() => setAdding(true)}>
                            <Plus className="w-4 h-4 mr-1" /> New session
                        </Button>
                    )}
                </div>

                {adding && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1">Session name</label>
                                <Input placeholder="e.g. Nursing — H2 calibration"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Cycle</label>
                                <Select className="w-full" placeholder="Select a cycle"
                                    value={form.cycle_id}
                                    onChange={(v) => setForm({ ...form, cycle_id: v })}
                                    options={cycles.map((c) => ({ value: c.id, label: c.name }))} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Department</label>
                                <Select className="w-full" allowClear placeholder="All departments"
                                    value={form.department_id}
                                    onChange={(v) => setForm({ ...form, department_id: v ?? null })}
                                    options={departments.map((d) => ({
                                        value: d.id, label: d.department_name || d.name,
                                    }))} />
                                <p className="text-xs text-muted-foreground mt-1">
                                    A room of peers who manage comparable people calibrates better
                                    than one covering the whole company.
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Scheduled for</label>
                                <DatePicker className="w-full h-10" format="DD-MM-YYYY"
                                    value={form.scheduled_on ? dayjs(form.scheduled_on) : null}
                                    onChange={(d) => setForm({ ...form, scheduled_on: d ? d.format("YYYY-MM-DD") : null })} />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="brand" disabled={saving} onClick={save}>Create</Button>
                            <Button size="sm" variant="ghost" disabled={saving}
                                onClick={() => setAdding(false)}>Cancel</Button>
                        </div>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                    {loading ? (
                        <div className="text-center py-10 text-muted-foreground">Loading…</div>
                    ) : sessions.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">
                            No calibration sessions yet.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {sessions.map((s) => (
                                <button key={s.id} type="button"
                                    onClick={() => navigate(`/appraisal/calibration/${s.id}`)}
                                    className="w-full text-left border border-border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-900/40">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div>
                                            <div className="font-medium">{s.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {s.cycle_name} · {s.department_name}
                                                {s.scheduled_on && ` · ${dayjs(s.scheduled_on).format("DD-MM-YYYY")}`}
                                            </div>
                                        </div>
                                        <span className={cn("text-xs px-2 py-0.5 rounded-full",
                                            STATUS_TONE[s.status] || STATUS_TONE.draft)}>
                                            {String(s.status || "").replace("_", " ")}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
};

export default CalibrationSessionList;
